import {
  Alert,
  ActionList,
  ActionListItem,
  Box,
  Button,
  Dropdown,
  DropdownOverlay,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectInput,
  Spinner,
  SearchInput,
  Text,
  DatePicker,
} from '@razorpay/blade/components';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { NewCustomWindow } from './types';
import { FieldContainerV2 } from './styles';
import Errors from 'components/ui/Form/Errors';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import api from 'api';
import { CreateCustomWindowModalV2Props, EmployeeOption } from './types';
import { FBPSettingsDescriptions } from 'components/Settings/FlexibleBenefitsSettings/constants';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';

const CreateCustomWindowModalV2 = ({
  isOpen,
  onDismiss,
  onSubmit,
  isLoading,
  customWindowModalCondition,
  creationErrors,
  employees = [],
  isLoadingEmployees = false,
}: CreateCustomWindowModalV2Props) => {
  const { isM3S2Enabled } = useIsFeatureDisabledForRearch();
  const [data, setData] = useState<{
    startDate: Date | null;
    endDate: Date | null;
    type: string;
    employeeIds?: string[];
  }>({
    startDate: null,
    endDate: null,
    type: '',
    employeeIds: [],
  });

  const [searchValue, setSearchValue] = useState('');
  const { debouncedValue: debouncedSearchValue } = useDebouncedValue(searchValue, 150);
  const searchEmployeesQuery = useQuery({
    queryKey: ['search-employees-override', debouncedSearchValue],
    queryFn: () => api.search.searchEmployees(debouncedSearchValue),
    enabled: !!debouncedSearchValue && debouncedSearchValue.length > 2,
  });

  const employeeList = React.useMemo(() => {
    const searchResults: EmployeeOption[] =
      searchEmployeesQuery.data?.map((result: any) => ({
        id: result.people_id || result.id,
        name: result.name,
        email: result.email || '',
        employeeId:
          result.employee_id || result.employeeId || String(result.people_id || result.id),
        designation: result.designation,
        department: result.department,
      })) || [];

    if (searchResults.length > 0) {
      const searchResultIds = new Set(searchResults.map((emp) => emp.id));
      const remainingEmployees = employees.filter((emp) => !searchResultIds.has(emp.id));
      return [...searchResults, ...remainingEmployees];
    }

    return employees;
  }, [employees, searchEmployeesQuery.data]);

  const handleSearchChange = React.useCallback(({ value }: { value?: string }) => {
    setSearchValue(value || '');
  }, []);

  const handleClearSearch = React.useCallback(() => {
    setSearchValue('');
  }, []);

  const doesErrorExist =
    customWindowModalCondition &&
    !!customWindowModalCondition(data.startDate, data.endDate, data.type);

  const isValid =
    !!data.endDate &&
    !!data.startDate &&
    !!data.type &&
    !!data.employeeIds &&
    data.employeeIds.length > 0 &&
    data.endDate >= data.startDate &&
    !doesErrorExist;

  const handleCreateWindowClick = () => {
    if (isValid) {
      const submitData: NewCustomWindow = {
        startDate: data.startDate!,
        endDate: data.endDate!,
        type: data.type,
        employees: data.employeeIds?.join(', ') || '',
      };
      onSubmit(submitData);
    }
  };

  const clearData = () => {
    setData({
      startDate: null,
      endDate: null,
      type: '',
      employeeIds: [],
    });
    setSearchValue('');
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={() => {
        clearData();
        onDismiss();
      }}
      size="medium">
      <ModalHeader
        title="Create new window"
        subtitle={
          FBPSettingsDescriptions['custom-window-settings-description'][
            isM3S2Enabled ? 'M3S2' : 'DEFAULT'
          ]
        }
      />
      <ModalBody>
        {customWindowModalCondition &&
          customWindowModalCondition(data.startDate, data.endDate, data.type) && (
            <Alert
              isDismissible={false}
              description={customWindowModalCondition(data.startDate, data.endDate, data.type)}
              isFullWidth
              marginBottom={'spacing.7'}
              color="notice"
            />
          )}
        {creationErrors && (
          <Alert
            isDismissible={false}
            description={<Errors errorResponse={creationErrors} />}
            isFullWidth
            marginBottom={'spacing.7'}
            color="negative"
          />
        )}
        <Box display="flex" flexDirection="column" gap="spacing.7">
          <FieldContainerV2>
            <Dropdown selectionType="multiple">
              <SelectInput
                label="Add employee(s)"
                placeholder={isLoadingEmployees ? 'Loading employees...' : 'Search Employee'}
                name="employeeIds"
                value={data.employeeIds}
                isDisabled={isLoadingEmployees}
                onChange={({ values }) => setData((prev) => ({ ...prev, employeeIds: values }))}
              />
              <DropdownOverlay>
                <Box
                  padding="spacing.5"
                  borderBottomWidth="thin"
                  borderBottomColor="surface.border.gray.subtle">
                  <SearchInput
                    label=""
                    value={searchValue}
                    placeholder="Search by name, email or employee ID"
                    onClearButtonClick={handleClearSearch}
                    onChange={handleSearchChange}
                  />
                </Box>
                {isLoadingEmployees || searchEmployeesQuery.isLoading ? (
                  <Box display="flex" justifyContent="center" padding="spacing.7">
                    <Spinner accessibilityLabel="Loading employees" />
                  </Box>
                ) : employeeList.length > 0 ? (
                  <ActionList>
                    {employeeList.map((employee) => (
                      <ActionListItem
                        key={employee.id}
                        title={employee.name}
                        description={`${employee.employeeId}`}
                        value={String(employee.id)}
                      />
                    ))}
                  </ActionList>
                ) : (
                  <Box padding="spacing.7" textAlign="center">
                    <Text color="surface.text.gray.muted">
                      {searchValue
                        ? 'No employees found matching your search'
                        : 'Search employee to continue'}
                    </Text>
                  </Box>
                )}
              </DropdownOverlay>
            </Dropdown>
            <Dropdown selectionType="single">
              <SelectInput
                label="Type"
                placeholder="Select window"
                name="action"
                value={data.type}
                onChange={({ values }) => setData((prev) => ({ ...prev, type: values[0] }))}
              />
              <DropdownOverlay>
                <ActionList>
                  <ActionListItem title="Declaration window" value="declaration_window" />
                  <ActionListItem title="Proof upload window" value="proof_upload_window" />
                </ActionList>
              </DropdownOverlay>
            </Dropdown>
            <Box
              display="flex"
              flexDirection="row"
              gap={'spacing.5'}
              justifyContent="space-between">
              <Box flex={1}>
                <DatePicker
                  value={data.startDate}
                  label="Open from"
                  inputPlaceHolder="Select date"
                  minDate={new Date()}
                  onChange={(date) => setData((prev) => ({ ...prev, startDate: date }))}
                />
              </Box>

              <Box flex={1}>
                <DatePicker
                  value={data.endDate}
                  label="Till"
                  inputPlaceHolder="Select date"
                  minDate={new Date()}
                  onChange={(date) => setData((prev) => ({ ...prev, endDate: date }))}
                />
              </Box>
            </Box>
          </FieldContainerV2>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" gap="spacing.3" justifyContent="flex-end" width="100%">
          <Button variant="secondary" onClick={onDismiss} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleCreateWindowClick} isLoading={isLoading} isDisabled={!isValid}>
            Create window
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export { CreateCustomWindowModalV2 };
